(() => {
  const root = document.querySelector('[data-es-shared-page]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';
  const strings = JSON.parse(root.querySelector('[data-es-shared-strings]').textContent);
  const grid = root.querySelector('[data-es-shared-grid]');
  const status = root.querySelector('[data-es-shared-status]');
  const recovery = root.querySelector('[data-es-shared-recovery]');
  const retry = root.querySelector('[data-es-shared-retry]');
  const original = root.querySelector('[data-es-shared-original]');
  const route = root.dataset.root.replace(/\/?$/, '/');
  const token = new URL(location.href).searchParams.get('share');
  const validToken = /^[a-f0-9]{32,128}$/i.test(token || '');
  const source = validToken ? '/a/magic-wishlist/shared/' + encodeURIComponent(token) : '';
  const products = new Map();
  let loading = false;

  async function request(url, type, credentials = 'same-origin') {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials, cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error(strings.error);
      return type === 'json' ? await response.json() : await response.text();
    } finally { clearTimeout(timer); }
  }

  function getRows(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.querySelector('.header .header-inner') || !doc.querySelector('.container .grid, .empty-state')) throw new Error(strings.error);
    const cards = [...doc.querySelectorAll('.container .grid .product-card')];
    if (!cards.length && doc.querySelector('.container .grid')?.children.length) throw new Error(strings.error);
    return cards.map(card => {
      const link = card.querySelector('.product-title a');
      const url = new URL(link?.getAttribute('href') || '', location.origin);
      const match = url.pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/([a-z0-9-]+)\/?$/i);
      if (url.origin !== location.origin || !match) throw new Error(strings.error);
      const variantId = card.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') || '';
      if (variantId && !/^\d+$/.test(variantId)) throw new Error(strings.error);
      return { handle: match[1], variantId, title: link.textContent.trim() };
    });
  }

  function product(handle) {
    if (!products.has(handle)) {
      const task = request(route + 'products/' + encodeURIComponent(handle) + '.js', 'json');
      products.set(handle, task);
      task.catch(() => products.delete(handle));
    }
    return products.get(handle);
  }

  function imageUrl(value, width) {
    if (!value) return '';
    const url = new URL(value, location.origin);
    if (url.protocol !== 'https:' || ![location.hostname, 'cdn.shopify.com'].includes(url.hostname)) return '';
    url.searchParams.set('width', width);
    return url.href;
  }

  async function renderRow(row) {
    let data;
    try { data = await product(row.handle); } catch { data = null; }
    const variant = data?.variants?.find(item => String(item.id) === row.variantId);
    const card = root.querySelector('[data-es-shared-card]').content.firstElementChild.cloneNode(true);
    const url = new URL(route + 'products/' + row.handle, location.origin);
    if (row.variantId) url.searchParams.set('variant', row.variantId);
    card.querySelectorAll('[data-es-shared-product-link]').forEach(link => { link.href = url.href; });
    const heading = card.querySelector('h2');
    if (root.dataset.titleClass) heading.classList.add(root.dataset.titleClass);
    heading.querySelector('a').textContent = data?.title || row.title;
    const variantText = card.querySelector('[data-es-shared-variant]');
    if (variant && variant.title !== 'Default Title') {
      variantText.textContent = variant.title;
      variantText.hidden = false;
    }
    const price = card.querySelector('[data-es-shared-price]');
    if (variant && Number.isFinite(variant.price)) {
      price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: root.dataset.currency }).format(variant.price / 100);
    } else { price.hidden = true; }
    const image = card.querySelector('img');
    const src = variant?.featured_image?.src || data?.featured_image;
    const image600 = imageUrl(src, 600);
    if (image600) {
      image.src = image600;
      image.srcset = imageUrl(src, 300) + ' 300w, ' + image600 + ' 600w';
      image.sizes = '(max-width: 699px) 45vw, (max-width: 999px) 30vw, 25vw';
      image.alt = data?.title || row.title;
    } else { image.hidden = true; }
    const action = card.querySelector('[data-es-shared-action]');
    if (variant?.available) {
      const element = document.createElement('product-form');
      const form = document.createElement('form');
      form.method = 'post';
      form.action = route + 'cart/add';
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'id';
      input.value = row.variantId;
      const quantity = document.createElement('input');
      quantity.type = 'hidden';
      quantity.name = 'quantity';
      quantity.value = String(variant.quantity_rule?.min || 1);
      const button = document.createElement('button');
      button.type = 'submit';
      button.className = 'button button--outline';
      button.textContent = strings.add;
      form.append(input, quantity, button);
      element.append(form);
      action.append(element);
    } else {
      const link = document.createElement('a');
      link.href = url.href;
      link.className = 'link';
      link.textContent = variant ? strings.unavailable : strings.view;
      action.append(link);
    }
    return card;
  }

  async function load() {
    if (loading) return;
    loading = true;
    retry.disabled = true;
    recovery.hidden = true;
    grid.setAttribute('aria-busy', 'true');
    status.textContent = strings.loading;
    try {
      if (!validToken) throw new Error(strings.invalid);
      original.href = source;
      original.hidden = false;
      const rows = getRows(await request(source, 'text', 'omit'));
      const cards = new Array(rows.length);
      let index = 0;
      await Promise.all(Array.from({ length: Math.min(4, rows.length) }, async () => {
        while (index < rows.length) {
          const current = index++;
          cards[current] = await renderRow(rows[current]);
        }
      }));
      grid.replaceChildren(...cards);
      status.textContent = rows.length ? strings.count.replace('__COUNT__', rows.length) : strings.empty;
    } catch (error) {
      grid.replaceChildren();
      status.textContent = error.message === strings.invalid ? strings.invalid : strings.error;
      recovery.hidden = false;
      retry.hidden = !validToken;
    } finally {
      grid.setAttribute('aria-busy', 'false');
      retry.disabled = false;
      loading = false;
    }
  }

  root.addEventListener('cart:error', event => { status.textContent = typeof event.detail?.error === 'string' ? event.detail.error : strings.error; });
  root.addEventListener('variant:add', () => { status.textContent = strings.added; });
  retry.addEventListener('click', load);
  load();
})();
